import { CommonEventsEnum } from '@hubxtech/hubx-client-analytics-package/lib/src/enum/common/commonEventsEnum';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { appConfig } from '@/app/providers/config';
import { auth as firebaseAuth } from '@/app/providers/firebase';
import Logo from '@/assets/logo.svg?react';
import { selectAuth, setUser } from '@/features/auth/store/auth';
import { mapFirebaseUserToUser } from '@/features/auth/utils/user';
import { useGetSubscriptionQuery } from '@/features/payment/services/subscription';
import { useLandingFlow } from '@/features/w2w/hooks/landing-flow';
import { useAppDispatch, useAppSelector, useMediaQuery } from '@/hooks';
import { usePopupStatus } from '@/lib/analytics/hooks/popup-status';
import { logEvent } from '@/lib/analytics/services/log-event';
import { logHubxEvent } from '@/lib/analytics/services/log-hubx-event';
import { customEventPush } from '@/lib/analytics/utils/custom-event-push';
import { Button, Modal, Stepper } from '@/uikit/components';

import { AccountDetail } from './AccountDetail/AccountDetail';
import { CreatePassword } from './CreatePassword/CreatePassword';
import { EmailConfirmation } from './EmailConfirmation/EmailConfirmation';
import styles from './UserUpdateModal.module.scss';
import { WelcomeApp } from './WelcomeApp/WelcomeApp';

interface UserUpdateModalProps {
  onClose?: () => void;
}

export const UserUpdateModal = ({
  onClose,
}: UserUpdateModalProps): React.ReactNode => {
  const { path } = useLandingFlow();

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 800px)');
  const [open, setOpen] = usePopupStatus('landing-meta');

  const { userInfo } = useAppSelector(selectAuth);
  const { data: subscription } = useGetSubscriptionQuery('subscription');

  const [step, setStep] = useState(() => {
    const savedStep = localStorage.getItem('step');
    return savedStep != null ? parseInt(savedStep, 10) : 0;
  });

  const handleAfterPurchaseInfo = (): void => {
    customEventPush({
      event_name: `after_purchase_info_v2`,
      currency: subscription?.currencyCode,
      amount: subscription?.items[0]?.price?.unitPrice.amount ?? '',
      product_id: subscription?.items[0]?.price?.id ?? '',
      paywallType: path ?? 'in_app',
      UserInfo: JSON.stringify({
        email: firebaseAuth.currentUser?.email ?? '',
        fullName: userInfo?.fullName ?? '',
      }),
    });
    logEvent('after_purchase_info', {
      currency: subscription?.currencyCode,
      amount: Number(subscription?.items[0]?.price?.unitPrice.amount) ?? 0,
      product_id: subscription?.items[0]?.price?.id ?? '',
      paywallType: path ?? 'in_app',
      UserInfo: JSON.stringify({
        email: firebaseAuth.currentUser?.email ?? '',
        fullName: userInfo?.fullName ?? '',
      }),
    });
    logHubxEvent({
      eventName: CommonEventsEnum.AFTER_PURCHASE_INFO,
      event_params: {
        currency: subscription?.currencyCode,
        amount: Number(subscription?.items[0]?.price?.unitPrice.amount) ?? 0,
        product_id: subscription?.items[0]?.price?.id ?? '',
        paywallType: path ?? 'in_app',
        UserInfo: JSON.stringify({
          email: firebaseAuth.currentUser?.email ?? '',
          fullName: userInfo?.fullName ?? '',
        }),
      },
    });
  };

  const handleNextStep = (): void => {
    if (step === renderComponents.length - 1) {
      handleAfterPurchaseInfo();
      localStorage.removeItem('step');
      void setOpen(false);
      onClose?.();
      return;
    }
    if (
      firebaseAuth.currentUser !== null &&
      (renderComponents[step].updateUser ?? false)
    ) {
      const mappedUser = mapFirebaseUserToUser(firebaseAuth.currentUser);
      dispatch(setUser(mappedUser));
    }

    const nextStep = step + 1;
    setStep(nextStep);

    localStorage.setItem('step', nextStep.toString());
  };

  const handleSkip = (): void => {
    const nextStep = step + 1;
    setStep(nextStep);

    localStorage.setItem('step', nextStep.toString());
  };

  const renderComponents = [
    {
      component: <EmailConfirmation onSubmit={handleNextStep} />,
      title: t('userUpdate.emailVerification.title'),
      description: t('userUpdate.emailVerification.description'),
      updateUser: true,
    },
    {
      component: <CreatePassword onSubmit={handleNextStep} />,
      title: t('userUpdate.createPassword.title'),
      description: t('userUpdate.createPassword.description'),
    },
    {
      component: <AccountDetail onSubmit={handleNextStep} />,
      title: t('userUpdate.accountDetail.title'),
      description: t('userUpdate.accountDetail.description'),
      optional: true,
      updateUser: true,
    },
    {
      component: <WelcomeApp onSubmit={handleNextStep} />,
      title: `${t('userUpdate.getStarted.title')} ${appConfig.name}`,
    },
  ];

  useEffect(() => {
    if (path === 'landing-meta') {
      localStorage.setItem('step', step.toString());
    }
  }, [path, step]);

  return (
    <Modal
      className={styles.modal}
      fullscreen={isMobile}
      width={427}
      closedButton={step < renderComponents.length - 1}
      open={open ?? false}
      overlayClose={false}
      onClose={() => {
        handleNextStep();
      }}
      content={
        <div className={styles.container}>
          {step < renderComponents.length - 1 ? (
            <Stepper
              currentStep={step}
              steps={[
                t('userUpdate.emailVerification.name'),
                t('userUpdate.createPassword.name'),
                t('userUpdate.accountDetail.name'),
                t('userUpdate.getStarted.name'),
              ]}
            />
          ) : (
            <div className={styles['container__logo-content']}>
              <Logo className={styles.container__logo} />
            </div>
          )}
          <div>
            <div className={styles.container__component}>
              <div className={styles['container__title-area']}>
                <h2 className={styles.container__title}>
                  {renderComponents[step].title}
                </h2>
                <p className={styles.container__description}>
                  {renderComponents[step].description}
                </p>
              </div>
              {renderComponents[step].component}
              {(renderComponents[step].optional ?? false) && (
                <Button
                  className={styles['container__skip-button']}
                  onClick={handleSkip}
                  variant="nostyle"
                >
                  {t('userUpdate.skip')}
                </Button>
              )}
            </div>
          </div>
        </div>
      }
    />
  );
};
